import React, { useState, useMemo } from 'react';
import { Card, DatePicker, message, Spin, Typography } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import api from '../api/api';
import dayjs from 'dayjs';
import helpers from '../utils/helpers';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';


import chooseColor from '../utils/color';
import usePageMeta from '../hooks/usePageMeta';



function DayChart() {
  const { Title, Text } = Typography;
  usePageMeta('單日分數變化 | 光暈戰記排行榜', 'https://twilightwarscloudflarereact.pages.dev/daily-chart');
  const minDate = dayjs(helpers.DB_START_DATE);
  const maxDate = dayjs(); // 今天
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState([]);
  const [players, setPlayers] = useState([]);
  const [visiblePlayers, setVisiblePlayers] = useState({});


  // 將被隱藏玩家的數值轉為 null，保留 Line 與 Legend，確保可再點回來顯示
  const displayedPoints = useMemo(() => {
    if (!points || players.length === 0) return points;
    return points.map(row => {
      const copy = { ...row };
      players.forEach(p => {
        if (!visiblePlayers[p.key]) {
          copy[p.key] = null;
        }
      });
      return copy;
    });
  }, [points, players, visiblePlayers]);

  const fetchDay = async (d) => {
    if (!d) return;
    const dateString = d.format('YYYY-MM-DD');
    setLoading(true);
    try {
      const res = await api.getDailyHours(dateString);
      const windows = res.windows || [];

      if (!windows || windows.length === 0) {
        message.info('沒有該日資料');
        setPoints([]);
        setPlayers([]);
        return;
      }

      const playerMap = new Map();
      windows.forEach(w => {
        const hr = w.hour;
        (w.data || []).forEach(r => {
          const id = r.userid ?? r.user ?? r.id ?? (r.nickname || 'unknown');
          const label = r.nickname ?? String(id);
          if (!playerMap.has(id)) playerMap.set(id, { label, hours: {} });
          playerMap.get(id).hours[hr] = r.mark;
        });
      });

      const playerEntries = Array.from(playerMap.entries());
      const preparedPlayers = playerEntries.map(([id, { label }]) => ({
        id,
        label,
        key: `u_${String(id)}`,
        color: chooseColor(id),
      }));

      const pts = windows.map(w => {
        const base = { hour: w.hour };
        preparedPlayers.forEach(p => {
          const val = playerMap.get(p.id)?.hours?.[w.hour];
          base[p.key] = typeof val === 'number' ? val : null;
        });
        return base;
      });

      const initialVisibility = preparedPlayers.reduce((acc, player) => {
        acc[player.key] = true;
        return acc;
      }, {});
      setVisiblePlayers(initialVisibility);

      setPoints(pts);
      setPlayers(preparedPlayers);
    } catch (err) {
      console.error('fetch day error', err);
      message.error('取得資料失敗，請稍後再試');
      setPoints([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLegendClick = (e) => {
    const dataKey = e?.dataKey || e?.payload?.dataKey;
    if (!dataKey) return;
    setVisiblePlayers(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };


  // Custom legend: shows swatch, label (toggles), and a small external link icon
  // clicking the link will open the user's page in a new tab and won't toggle visibility
  const CustomLegend = ({ payload = [] }) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 12 }}>
        {payload.map((entry) => {
          const { dataKey, value, color } = entry || {};
          const player = players.find(p => p.key === dataKey);
          const isVisible = !!visiblePlayers[dataKey];
          const style = isVisible
            ? { color: '#333', cursor: 'pointer' }
            : { color: '#ccc', textDecoration: 'line-through', cursor: 'pointer' };
          const userLink = player ? `https://gamelet.online/user/${player.id}/` : null;

          return (
            <div
              key={dataKey}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px' }}
            >
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 6, background: color || '#ddd' }} />

              {/* 名稱負責切換；支援 click / touch / keyboard，以避免連結誤觸發父容器 */}
              <span
                role="button"
                tabIndex={0}
                onClick={() => handleLegendClick({ dataKey })}
                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLegendClick({ dataKey }); }}
                onTouchStart={() => handleLegendClick({ dataKey })}
                style={style}
              >
                {value}
              </span>

              {userLink && (
                <a
                  href={userLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { e.stopPropagation(); }}
                  onTouchStart={(e) => { e.stopPropagation(); }}
                  title={`開啟 ${player.label} 的個人頁面`}
                  style={{ marginLeft: 6, color: '#888', textDecoration: 'none' }}
                >
                  🔗
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };


  return (
    <div style={{ padding: '1.25rem' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <Title level={2} style={{ margin: 0 }}>
            <TrophyOutlined style={{ marginRight: '0.5rem', color: '#FFD700' }} />
            單日分數變化
          </Title>
        </div>

        <div style={{ fontSize: 12, marginTop: -8, marginBottom: 12 }}>
          <Text> 提示：點擊下方圖例（玩家名稱）可顯示/隱藏線條。被隱藏時會變灰並帶刪除線，再點一次即可顯示回來。</Text>

        </div>

        <DatePicker
          format="YYYY-MM-DD"
          inputReadOnly={true}
          disabledDate={(current) => current < minDate || current > maxDate}
          onChange={(d) => fetchDay(d)} placeholder="請選擇日期" style={{ marginBottom: '1rem' }} />

        <div style={{ minHeight: 360 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={500}>
                <LineChart data={displayedPoints} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tickFormatter={(tick) => `${tick}:55`} />
                  <YAxis />
                  <Tooltip />


                  {/* Legend is rendered separately to avoid it occupying chart's internal space and
          potentially overlapping the plotting area on small screens. */}

                  {players.map(p => (
                    <Line
                      key={p.key}
                      type="monotone"
                      dataKey={p.key}
                      name={p.label}
                      stroke={p.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>

              {/* render legend outside of chart to avoid overlapping */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <CustomLegend payload={players.map(p => ({ dataKey: p.key, value: p.label, color: p.color }))} />
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export default DayChart;


